import React, { useMemo } from 'react';
import { CellProps, Column, useExpanded, useFilters, useTable } from 'react-table';
import { IBalanceInstruction } from '../../../../../sp-types/balanceFlowType';
import { ExecutionTableBody } from './ExecutionTableComponents.tsx/ExecutionTableBody';
import { ExecutionTableFooter } from './ExecutionTableComponents.tsx/ExecutionTableFooter';
import { ExecutionTableHeader } from './ExecutionTableComponents.tsx/ExecutionTableHeader';
import { capitalFirst, formatToShortString } from '@utils/FormatValue';

const MAX_2_DIGIT = { maximumFractionDigits: 2 };
const useColumns = (isShowExecuted: boolean): Column<IBalanceInstruction>[] => {
  return useMemo(
    () =>
      [
        {
          Header: 'Transaction',
          accessor: 'action',
          Cell: ({ value }: CellProps<IBalanceInstruction, string>) => <span>{capitalFirst(value)}</span>,
          Footer: <span>Total</span>,
        },
        {
          Header: 'Expiry date',
          accessor: (row: IBalanceInstruction) => row,
          Cell: ({ value }: CellProps<IBalanceInstruction, IBalanceInstruction>) => (
            <span>{value.expiryDate.toDate().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
          ),
        },
        {
          Header: 'Instrument',
          accessor: (row: IBalanceInstruction) => row.instrumentId,
        },
        {
          Header: 'No. of contracts',
          accessor: 'numberOfContracts',
          Footer: (info) => {
            const total = info.data.reduce(
              (sum: number, row: any) =>
                sum + (isShowExecuted ? row.executedNumberOfContracts || 0 : row.numberOfContracts),
              0,
            );
            return <span>{total}</span>;
          },
          width: 200,
        },
        {
          Header: 'Contract weight', // TODO: add materials map, and unit
          accessor: (row: IBalanceInstruction) => row,
          Cell: ({ value }: CellProps<IBalanceInstruction, IBalanceInstruction>) => (
            <span>{formatToShortString(value.quantityPerContract)}</span>
          ),
        },

        {
          Header: 'Last price',
          accessor: 'lastPrice',
          Cell: (item: CellProps<IBalanceInstruction, number>) => <span>{`$${formatToShortString(item?.value)}`}</span>,
          width: 200,
        },
        {
          Header: 'Maximum price',
          accessor: 'maximumPrice',
          Cell: ({ value }: CellProps<IBalanceInstruction, number>) => <span>{`$${formatToShortString(value)}`}</span>,
        },

        {
          Header: 'Quantity',
          accessor: (row: IBalanceInstruction) => row,
          Cell: ({ value }: CellProps<IBalanceInstruction, IBalanceInstruction>) => (
            <span>{formatToShortString(value.numberOfContracts * value.quantityPerContract)}</span>
          ),
          width: 100,
          Footer: (info) => {
            const total = info.rows.reduce(
              (sum, { original: { executedNumberOfContracts, numberOfContracts, quantityPerContract } }) =>
                sum + (isShowExecuted ? executedNumberOfContracts || 0 : numberOfContracts) * quantityPerContract,
              0,
            );
            return <span>{formatToShortString(Math.floor(total))}</span>;
          },
        },
        {
          Header: 'Total Price',
          accessor: (row: IBalanceInstruction) => row,
          Cell: ({ value }: CellProps<IBalanceInstruction, IBalanceInstruction>) => (
            <span>
              $
              {(value.quantityPerContract * value.numberOfContracts * value.lastPrice).toLocaleString(
                undefined,
                MAX_2_DIGIT,
              )}
            </span>
          ),
          Footer: (info) => {
            const total = info.rows.reduce(
              (sum, row) =>
                isShowExecuted
                  ? sum +
                    (row.original.executedNumberOfContracts || 0) *
                      (row.original.executedPrice || 1) *
                      row.original.quantityPerContract
                  : sum + row.original.numberOfContracts * row.original.lastPrice * row.original.quantityPerContract,
              0,
            );

            return <span>${total.toLocaleString(undefined, MAX_2_DIGIT)}</span>;
          },
        },
      ] as Column<IBalanceInstruction>[],
    [isShowExecuted],
  );
};
interface Props {
  tableData: IBalanceInstruction[];
  onUpdateExecution?: (_instrument: string, _contractCount?: number, _price?: number) => void;
  isShowExecuted?: boolean;
}

export const ExecutionTable: React.FC<Props> = ({ tableData, onUpdateExecution, isShowExecuted = false }) => {
  const columns = useColumns(isShowExecuted);

  const tableInstance = useTable({ columns, data: tableData }, useFilters, useExpanded);

  const { getTableBodyProps, headerGroups, rows, footerGroups, prepareRow } = tableInstance;

  return (
    <div className="flex flex-1 overflow-auto   ">
      <table className="flex-1 overflow-auto ">
        <ExecutionTableHeader headerGroups={headerGroups} />
        <ExecutionTableBody
          isShowExecuted={isShowExecuted}
          rows={rows}
          prepareRow={prepareRow}
          getTableBodyProps={getTableBodyProps}
          onUpdateExecution={onUpdateExecution}
        />
        <ExecutionTableFooter footerGroups={footerGroups} />
      </table>
    </div>
  );
};
